/** Relatos event kinds. Custom kinds chosen to avoid colliding with existing NIPs. */
export const KIND_BOARD = 34553;   // parameterized replaceable, d = board id, admin-signed
export const KIND_POST = 1113;     // regular: threads and replies, content = board ciphertext
export const KIND_KEYUPDATE = 1114; // regular: admin -> member epoch salt distribution (NIP-44)
export const KIND_JOIN = 1115;     // regular: member join announcement (content encrypted)
export const KIND_PRESENCE = 21113; // ephemeral: session-only "online" ping
export const KIND_REACTION = 7;    // NIP-25, with blinded target tag per spec
export const KIND_DELETE = 5;      // NIP-09, blinded tags + board ciphertext per spec
export const KIND_GIFTWRAP = 1059; // NIP-59 wrap used for key distribution (kind 1114 rumor inside)
export const KIND_RESYNC = 1116;   // rumor-only (inside kind 1059): epoch-salt resync request/response
export const KIND_RELAYLIST = 30002; // parameterized replaceable, d = "relatos:<boardId>", admin-signed

/** Default public relays — DEVIATION from build plan Phase 2 (strfry+Tor),
 *  flagged in CLAUDE.md tradeoff log. Replace when the Phase-2 relay ships. */
export const DEFAULT_RELAYS = [
  // Privacy-friendly / unmoderated public relays (user-selected set).
  'wss://nostr-pub.wellorder.net',
  'wss://nostr.onsats.org',
  'wss://nostr.sebastix.site',
  'wss://nostr.fmt.wiz.biz',
  'wss://relay.bitid.nz',
  'wss://nostr.zerofeerouting.com',
  'wss://nostr-relay.wlvs.space',
  'wss://relay.nostrati.com',
  'wss://nostr.bongbong.bong',
  'wss://nostr.oxtr.dev',
  // High-uptime general relays kept for bootstrap reach.
  'wss://nos.lol',
  'wss://nostr.mom',
];

/** Forum admins: only these pubkeys may create PUBLIC boards (the directory
 *  would otherwise be spammed). Anyone can still create private invite-only
 *  boards, and post topics/replies in boards they hold keys for. */
export const ADMIN_PUBKEYS: string[] = [
  'c6e3a2096d95a8a8e6821df468bd91fb4688cb7aeec881431e40172c991b62c1', // deployment owner
];

export const isForumAdmin = (pubkey: string) => ADMIN_PUBKEYS.includes(pubkey);

/** Moderator pubkey receiving encrypted post reports (kind 1984 rumor inside
 *  a NIP-59 gift wrap, so relays can't see who reported what). */
export const MODERATOR_PUBKEY = 'c6e3a2096d95a8a8e6821df468bd91fb4688cb7aeec881431e40172c991b62c1';

export const KIND_REPORT = 1984;   // NIP-56 report — used rumor-only inside kind 1059 wraps

// --- encrypted payload shapes (inside NIP-44 board ciphertext) -----------
// board/epoch fields: in-payload context binding (Concord-style). Readers
// strict-equal check them when present; absent = legacy event, accepted.

export interface PostPayload {
  t: 'post';
  kind: 'thread' | 'reply';
  subject?: string;      // thread roots only
  body: string;          // BBCode
  root?: string;         // replies: real event id of thread root
  parent?: string;       // replies: real event id of direct parent
  board?: string;        // context binding
  epoch?: number;        // context binding
}

export interface ReactPayload {
  t: 'react';
  target: string;        // real event id
  value: string;         // '+' for now
  board?: string;
  epoch?: number;
}

export interface DeletePayload {
  t: 'delete';
  target: string;        // real event id of the post to delete
  board?: string;
  epoch?: number;
}

export interface JoinPayload {
  t: 'join';
  name?: string;
  board?: string;
  epoch?: number;
  /** Author's identity pubkey, inside the ciphertext: lets the admin map the
   *  per-board pseudonymous event key back to a member for epoch-salt
   *  distribution. Visible to members (they hold the epoch key), never to
   *  relays — the relay only sees the pseudonymous outer pubkey. */
  realPk?: string;
}

/** Member -> admin (gift-wrapped rumor): "I'm stuck at epoch N, resend salts". */
export interface ResyncReqPayload {
  t: 'resync-req';
  board: string;
  haveEpoch: number;
}

/** Admin -> member (gift-wrapped rumor): every salt above haveEpoch. */
export interface ResyncKeysPayload {
  t: 'resync-keys';
  board: string;
  salts: Record<number, string>; // epoch -> salt that derived that epoch
}

/** Member -> moderator (gift-wrapped kind-1984 rumor): post report. The
 *  reporter is a board member, so the excerpt is plaintext THEY decrypted —
 *  sending it is their explicit choice. */
export interface ReportPayload {
  t: 'report';
  board: string;        // board id
  target: string;       // reported post event id
  author: string;       // author's per-board pseudo pubkey
  subject?: string;
  excerpt?: string;     // first ~500 chars of the decrypted body
  reason?: string;
  reportedAt: number;   // unix seconds
}

export interface KeyUpdatePayload {
  t: 'keyupdate';
  board: string;
  epoch: number;
  salt: string;
}

export interface PresencePayload {
  t: 'presence';
  until: number;
}

export interface RelayListPayload {
  t: 'relays';
  relays: string[];      // board relay set (union'ed with defaults by readers)
  updatedAt: number;     // unix seconds — readers apply only if newer than current
}
